<template>
  <div>
    <ValidationObserver ref="validator" slim v-slot="{ handleSubmit, passed }">
      <p class="electricity-popup-form title">{{ $t('meter-box.electricalMeter') }}</p>
      <div class="row">
        <div class="col-xs-12 col-sm-6 col-md-4">
          <number-box :label="$t('meter.form.count')" :min="1" :step="1" required v-model="elecMeter.count"></number-box>
        </div>
      </div>
      <div class="row">
        <div class="col-xs-12 col-sm-6">
          <select-box :label="$t('meter.form.tension')" :options="tensions.map((t) => { return { text: $t('meter.tension.' + t), value: t }})" required v-model="elecMeter.tension"></select-box>
        </div>
        <div class="col-xs-12 col-sm-6" v-if="!isCustom">
          <power-select-box :label="$t('meter.form.power')" :options="powers.map((p) => { return { text: p, value: p }})" :tension="elecMeter.tension" required v-model.number="ampere"></power-select-box>
        </div>
        <template v-else>
          <div class="col-xs-12 col-sm-6">
            <number-box :label="$t('meter.form.power')" :min="0" :unit="$t('energyProduction.kva')" required v-model.number="kva"></number-box>
          </div>
        </template>
      </div>
      <div class="row" v-if="!isCustom">
        <div class="col-xs-12 col-sm-6">
          <select-box :label="$t('meter.form.type')" :options="tarificationsOptions" required v-model="elecMeter.type"></select-box>
        </div>
        <div class="col-xs-12 col-sm-6">
          <select-box :label="$t('meter.form.destination')" :options="destinations.map((d) => { return { text: $t('meter.destination.' + d), value: d }})" required v-model="elecMeter.destination"></select-box>
        </div>
      </div>
      <collapsable-text-area :max='500' :add-label="$t('meter.form.remarks')" :label="$t('meter.form.remarks')" v-model="elecMeter.remarks"></collapsable-text-area>
      <div class="lib__text-center">
        <a :class="{ 'lib__btn--disabled': !passed }" @click="handleSubmit(validateMeter)" class="lib__btn lib__btn--primary lib__btn--rounded" href="#">
          <slot>
            <i class="fas fa-check"></i>
            <span>{{ $t('common.confirm') }}</span>
          </slot>
        </a>
      </div>
    </ValidationObserver>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch } from 'vue-property-decorator'
import { ElectricityMeter, MeterElectricityTension, MeterElectricityType, MeterDestination } from '@/models'
import { meterService } from '@/services/meter-service'
import PowerSelectBox from '@/components/inputs/Configuration/PowerSelectBox.vue'
import { helper } from '@/services'

@Component({
  components: {
    PowerSelectBox
  },
  name: 'cmh-electricity-popup-form'
})
export default class CmhElectricityPopupForm extends Vue {
  //#region [Property]
  @Prop({ type: Object, required: true }) public electricityMeter!: ElectricityMeter
  @Prop({ type: Array, required: true }) public tarifications!: MeterElectricityType[]
  @Prop({ type: Array, required: true }) public destinations!: MeterDestination[]
  @Prop({ type: Array, required: true }) public tensions!: MeterElectricityTension[]
  //#endregion

  //#region [Data]
  public elecMeter: ElectricityMeter = {
    tension: 'mono230V',
    count: 1,
    power: { ampere: 40, kva: 9.2 },
    destination: 'residential',
    type: 'dual',
    remarks: null,
    wantEmergencyPower: false,
    price: 765
  }
  //#endregion

  //#region [Method]
  public mounted() {
    if (this.electricityMeter != null) {
      this.elecMeter = helper.clone(this.electricityMeter)
    }
  }

  public validateMeter() {
    this.$emit('validateMeter', this.elecMeter)
  }
  //#endregion

  //#region [Computed]
  public get powers(): number[] {
    return meterService.getIntensities(this.elecMeter.tension)
  }

  public get isCustom(): boolean {
    return this.elecMeter.tension === 'custom'
  }

  public get ampere(): number | null {
    return this.elecMeter.power ? this.elecMeter.power.ampere : null
  }

  public set ampere(value: number | null) {
    if (!value) {
      this.elecMeter.power = null
    } else if (this.elecMeter.tension !== 'custom') {
      this.elecMeter.power = {
        ampere: value,
        kva: meterService.getPower(this.elecMeter.tension, value)
      }
    }
  }

  public get kva(): number | null {
    return this.elecMeter.power ? this.elecMeter.power.kva : null
  }

  public set kva(value: number | null) {
    if (!value) {
      this.elecMeter.power = null
    } else if (this.elecMeter.tension === 'custom') {
      this.elecMeter.power = {
        ampere: 0,
        kva: value
      }
    }
  }

  public get tarificationsOptions(): Array<{ text: string; value: string }> {
    return this.tarifications.map((t) => {
      return { text: this.$t('meter.type.' + t) as string, value: t }
    })
  }
  //#endregion

  //#region [Watch]
  @Watch('electricityMeter')
  public electricityMeterChange(value: ElectricityMeter) {
    this.elecMeter = helper.clone(this.electricityMeter)
  }

  @Watch('elecMeter.tension')
  public onTensionChange(value: MeterElectricityTension) {
    if (value === 'custom') {
      this.elecMeter.destination = null
      this.elecMeter.type = null
      if (this.ampere) {
        this.elecMeter.power = null
      }
    } else {
      const ampere = this.ampere
      const intensities = meterService.getIntensities(value)

      if (!ampere || intensities.indexOf(ampere) === -1) {
        this.ampere = intensities[0]
      } else {
        this.ampere = ampere
      }
    }
  }
  //#endregion
}
</script>

<style scoped>
.electricity-popup-form.title {
  height: 16px;
  width: 300px;
  color: #88949b;
  font-family: 'Open Sans';
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0;
  line-height: 16px;
}
</style>
